import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DraftFactConflict } from "@/types/drafting";

interface ConflictResolutionPanelProps {
  conflicts: DraftFactConflict[];
  onResolve: (conflictId: string, value: string) => Promise<void>;
  isResolving?: boolean;
}

export function ConflictResolutionPanel({
  conflicts,
  onResolve,
  isResolving = false,
}: ConflictResolutionPanelProps) {
  const openConflicts = conflicts.filter((c) => c.status === "open");

  if (!openConflicts.length) {
    return null;
  }

  return (
    <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 dark:border-amber-500/30">
      <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
        Resolve fact conflicts
      </h3>
      <p className="text-xs text-amber-800/80 dark:text-amber-200/80">
        Conflicting values were found across your inputs. Select the correct value for each field
        before generating the draft.
      </p>
      {openConflicts.map((conflict) => (
        <div
          key={conflict.id}
          className="rounded-lg border border-amber-500/20 bg-white/60 p-3 dark:bg-slate-900/40"
        >
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
            {conflict.field}
          </p>
          <div className="flex flex-wrap gap-2">
            {conflict.candidates.map((candidate) => (
              <Button
                key={`${conflict.id}-${candidate.value}`}
                type="button"
                size="sm"
                variant="outline"
                disabled={isResolving}
                onClick={() => onResolve(conflict.id, candidate.value)}
                className={cn("h-auto flex-col items-start gap-0.5 py-2 text-left")}
              >
                <span className="text-sm font-medium">{candidate.value}</span>
                <span className="text-[10px] text-[var(--lm-muted)] dark:text-slate-400">
                  from {candidate.source}
                </span>
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
